import { WarningTriangleIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import React, { useMemo } from "react";
import { LineItem, PaymentLineItem, PaymentMethod, Purchase, RemoteCard } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { isExpired } from "../../../models/RemoteCard";

export const PaymentCardInfo = ({ lineItem, purchase }: { lineItem: LineItem; purchase: Purchase }) => {
    const seller = useSeller();
    const paymentLineItem = lineItem as PaymentLineItem;
    const isStoreCreditRefund = !!paymentLineItem?.refundToStoreCredit;
    const isCreditCard = paymentLineItem.payment?.method === PaymentMethod.CREDIT_CARD && !isStoreCreditRefund;

    // we can get the last 4 digits & expiry details from paymentlineItem meta and we should use that
    // instead of fetching cards from traveler, since if we unlink any card from the traveler, they won't
    // appear even though we make a payment with that card, so in that case,we were displaying wrong card info.
    const lineItemMetaCard = isCreditCard ? paymentLineItem.payment?.meta?.cardSummary : null;

    // only fetch incase the card doesn't exist in meta
    const { data: savedCards } = useCollection<RemoteCard>(["purchases", purchase.id, "remoteCards"], null, {
        staleTime: 1000,
        keepPreviousData: true,
        enabled: isCreditCard && !lineItemMetaCard,
    });

    const card = useMemo(() => {
        if (!isCreditCard) {
            return null;
        }

        if (lineItemMetaCard) {
            return lineItemMetaCard;
        }

        return savedCards.find((savedCard) => savedCard.id === paymentLineItem.payment?.card);
    }, [savedCards, paymentLineItem, isCreditCard, lineItemMetaCard]);

    if (!card) {
        return null;
    }

    return (
        <>
            <span>
                {isExpired(card, seller.timezoneName) && (
                    <ExpireCardIcon content={`Card expired on ${card.expMonth}/${card.expYear}`} />
                )}
            </span>
            <span>*{card.last4}</span>
        </>
    );
};

const ExpireCardIcon = ({ content }: { content: string }) => {
    return (
        <Tooltip content={content}>
            <WarningTriangleIcon className="text-red" />
        </Tooltip>
    );
};
